// src/oura/components/KnowledgeEntry.tsx
//
// The pieces both knowledge surfaces share: the Knowledge tab on a business
// (ourteam-frontend#3684) and Step 2 of New scan.
//
// They are shared rather than written twice because the thing they describe is
// one thing. A file and a pasted note produce the same row, count against one
// budget and are read by the same prompts (ourteam-backend#4749), so two
// screens that disagree about how to add them would be describing a store that
// does not exist.

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
    AlertTriangleIcon,
    CheckCircle2Icon,
    ClipboardIcon,
    FileTextIcon,
    Loader2Icon,
    StickyNoteIcon,
    UploadCloudIcon,
    XIcon,
} from "lucide-react";
import type { AeoKnowledgeSource } from "@service/aeo";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../lib/cn";

/** What the file picker will accept, matching the backend's extractor list. */
export const KNOWLEDGE_ACCEPT = ".pdf,.docx,.txt,.md,.csv,.xlsx";

/**
 * How the row reads right now.
 *
 * Four states, and the fourth is the one worth having: a source can be `ready`
 * and still be wrong to trust, because `content_as_of` says the content was
 * true for a date over a year ago. A stale price in a client draft is the
 * expensive kind of wrong, so the age is on the badge rather than buried in a
 * chip nobody reads.
 */
export type KnowledgeState =
    | { kind: "ready"; label: string }
    | { kind: "stale"; label: string }
    | { kind: "reading"; label: string }
    | { kind: "failed"; label: string }
    | { kind: "excluded"; label: string };

const MONTH_MS = 30.44 * 24 * 60 * 60 * 1000;

export function knowledgeState(s: AeoKnowledgeSource, now = Date.now()): KnowledgeState {
    if (s.status === "pending") return { kind: "reading", label: "Reading" };
    if (s.status === "failed") return { kind: "failed", label: "Could not read" };
    if (s.status === "excluded") return { kind: "excluded", label: "Not used" };
    const asOf = s.contentAsOf ? Date.parse(s.contentAsOf) : NaN;
    if (!Number.isNaN(asOf)) {
        const months = Math.floor((now - asOf) / MONTH_MS);
        // Twelve months is the line the ticket draws. Below it the date is just
        // a fact on a chip; above it, it is a warning about the content itself.
        if (months > 12) return { kind: "stale", label: `Ready · ${months} months old` };
    }
    return { kind: "ready", label: "Ready" };
}

const STATE_CLASS: Record<KnowledgeState["kind"], string> = {
    ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
    stale: "border-amber-200 bg-amber-50 text-amber-700",
    reading: "border-gray-200 bg-gray-50 text-gray-600",
    failed: "border-rose-200 bg-rose-50 text-rose-700",
    excluded: "border-gray-200 bg-gray-50 text-gray-500",
};

export function KnowledgeStatusBadge({ state }: { state: KnowledgeState }) {
    return (
        <Chip className={cn("gap-1", STATE_CLASS[state.kind])}>
            {state.kind === "reading" && <Loader2Icon className="h-3 w-3 animate-spin" />}
            {state.kind === "ready" && <CheckCircle2Icon className="h-3 w-3" />}
            {(state.kind === "failed" || state.kind === "stale") && <AlertTriangleIcon className="h-3 w-3" />}
            {state.label}
        </Chip>
    );
}

function prettyBytes(n?: number | null): string | null {
    if (!n && n !== 0) return null;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function knowledgeDate(iso?: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * One source.
 *
 * The meta line is where a failure explains itself, and it carries the
 * backend's own sentence untouched — "This PDF is a scan…" tells an operator
 * what to do next, where "Upload failed" tells them nothing and invites a retry
 * that will fail the same way.
 */
export function KnowledgeRow({
    source,
    onDelete,
    onExclude,
    onOpen,
    deleteBlockedReason,
}: {
    source: AeoKnowledgeSource;
    /** Opens the source to read. Left out where there is nothing to open it in. */
    onOpen?: (source: AeoKnowledgeSource) => void;
    onDelete?: (id: string) => void | Promise<void>;
    onExclude?: (source: AeoKnowledgeSource) => void | Promise<void>;
    /**
     * Why Remove cannot run yet, if it cannot.
     *
     * Said out loud rather than hidden or faked: a staged row is already saved
     * against its domain and the scan adopts it, so dropping it from a list
     * without deleting it would tell the operator a file is gone while the
     * client's wrong price list still feeds every draft.
     */
    deleteBlockedReason?: string;
}) {
    const state = knowledgeState(source);
    const [busy, setBusy] = useState(false);
    const Icon = source.kind === "text" ? StickyNoteIcon : FileTextIcon;

    // The failure's own sentence wins the line: "This PDF is a scan. We could
    // not read any text from it." is what tells an operator what to do next,
    // where a size tells them nothing about a file nothing can read.
    //
    // `addedBy` is a user id, not a name, so it is not printed — a uuid under a
    // filename is noise. It stays on the type because the id is the useful half
    // for anything that has to resolve a person.
    const meta =
        state.kind === "failed" && source.errorDetail
            ? source.errorDetail
            : [
                source.kind === "text" ? "Pasted text" : prettyBytes(source.sizeBytes),
                source.charCount ? `${source.charCount.toLocaleString()} characters read` : null,
                knowledgeDate(source.createdAt) ? `added ${knowledgeDate(source.createdAt)}` : null,
            ].filter(Boolean).join(" · ");

    const run = (fn?: (...a: never[]) => unknown, ...args: unknown[]) => {
        if (!fn) return;
        setBusy(true);
        void Promise.resolve((fn as (...a: unknown[]) => unknown)(...args)).finally(() => setBusy(false));
    };

    return (
        <div className={cn("group relative flex items-start gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0", onOpen && "transition-colors hover:bg-gray-50/80")}>
            {/* The whole row opens it, under the buttons on the right, which
                stay their own targets above this one. */}
            {onOpen && (
                <button
                    type="button"
                    onClick={() => onOpen(source)}
                    aria-label={`Read ${source.title}`}
                    className="absolute inset-0 rounded-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500"
                />
            )}
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <Icon className="h-4 w-4 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <p className={cn("my-0 truncate text-sm font-medium text-gray-900", onOpen && "group-hover:text-blue-700 group-hover:underline")}>{source.title}</p>
                    <KnowledgeStatusBadge state={state} />
                </div>
                <p className={cn("my-0 mt-1 text-xs leading-relaxed", state.kind === "failed" ? "text-rose-700" : "text-gray-500")}>
                    {meta}
                </p>
                {/* While a source is being read there is no character count and
                    no outcome yet — a bar is the honest way to say "still
                    going" without inventing a percentage. */}
                {state.kind === "reading" && (
                    <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
                    </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {source.label && <Chip className="border-gray-200 bg-gray-50 text-gray-600">{source.label}</Chip>}
                    {knowledgeDate(source.contentAsOf) && (
                        <Chip className="border-gray-200 bg-gray-50 text-gray-600">as of {knowledgeDate(source.contentAsOf)}</Chip>
                    )}
                </div>
                {/* Rendered from the field, never invented. Its consumers are
                    out of scope on ourteam-backend#4749, so today this is always
                    empty — and an empty list must show nothing rather than
                    implying the source has been read by something. */}
                {!!source.usedBy?.length && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-gray-400">Used by</span>
                        {source.usedBy.map((u) => (
                            <Chip key={u} className="border-blue-200 bg-blue-50 text-blue-700">{u}</Chip>
                        ))}
                    </div>
                )}
            </div>
            <div className="relative flex flex-shrink-0 items-center gap-1">
                {onOpen && (
                    <Button variant="ghost" size="sm" onClick={() => onOpen(source)}>
                        Read
                    </Button>
                )}
                {onExclude && source.status !== "pending" && (
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        writes
                        onClick={() => run(onExclude, source)}
                        title={source.status === "excluded" ? "Use this source again" : "Stop using this source, keep the record"}
                    >
                        {source.status === "excluded" ? "Use again" : "Park"}
                    </Button>
                )}
                {onDelete && (
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !!deleteBlockedReason}
                        writes
                        title={deleteBlockedReason}
                        onClick={() => run(onDelete, source.id)}
                    >
                        Remove
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * A source that could NOT be added.
 *
 * Rose, and it carries the server's own sentence, because this one is a
 * failure: something the operator is holding did not land. The New scan modal
 * used to drop these silently — a picked file produced no row, no message and
 * no reason, which reads as the picker not having worked at all.
 */
export function KnowledgeProblemRow({
    title,
    message,
    onDismiss,
}: {
    title: string;
    message?: string;
    onDismiss?: () => void;
}) {
    return (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="my-0 flex-1 leading-relaxed">
                <span className="font-medium">{title}</span> could not be added.
                {message ? ` ${message}` : " Nothing was saved. Try again, or paste the text instead."}
            </p>
            {onDismiss && (
                <button onClick={onDismiss} className="flex-shrink-0 text-rose-700 hover:text-rose-900" aria-label="Dismiss">
                    <XIcon className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

/**
 * The row for a saved-knowledge list that could not be read. Same red as the
 * problem row, with a retry: the list was never empty, we just could not see it.
 */
export function KnowledgeListFailedRow({
    message,
    onRetry,
    retrying,
}: {
    message: string;
    onRetry: () => void;
    retrying?: boolean;
}) {
    return (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="my-0 flex-1 leading-relaxed">{message}</p>
            <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="flex flex-shrink-0 items-center gap-1 font-medium text-rose-700 hover:text-rose-900 disabled:opacity-60"
            >
                {retrying && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
                {retrying ? "Trying again…" : "Try again"}
            </button>
        </div>
    );
}

/** The 409 row. Amber, not red: nothing is wrong, the content is already here. */
export function KnowledgeDuplicateRow({ title, onDismiss }: { title: string; onDismiss?: () => void }) {
    return (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="my-0 flex-1 leading-relaxed">
                <span className="font-medium">{title}</span> is already here. Same file, same contents, added a moment ago.
                Nothing was added twice.
            </p>
            {onDismiss && (
                <button onClick={onDismiss} className="flex-shrink-0 text-amber-700 hover:text-amber-900" aria-label="Dismiss">
                    <XIcon className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}

/**
 * The two ways in, side by side and equal width.
 *
 * Never a toggle and never "Paste text instead". A toggle says you have to
 * choose; the common case is three PDFs *and* two pasted notes, and an earlier
 * version of this UI implied a choice that the store does not make.
 */
export function KnowledgeTiles({
    onFiles,
    onPaste,
    disabled,
}: {
    onFiles: (files: File[]) => void;
    onPaste: () => void;
    disabled?: boolean;
}) {
    const fileRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const writesPaused = useWritesPaused();
    return (
        <div className="grid grid-cols-1 gap-3 min-[700px]:grid-cols-2">
            <input
                ref={fileRef}
                type="file"
                multiple
                accept={KNOWLEDGE_ACCEPT}
                className="hidden"
                disabled={writesPaused}
                onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    // Cleared so picking the SAME file again still fires change.
                    e.target.value = "";
                    if (files.length) onFiles(files);
                }}
            />
            <button
                type="button"
                disabled={disabled || writesPaused}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const files = Array.from(e.dataTransfer.files ?? []);
                    if (files.length && !writesPaused) onFiles(files);
                }}
                className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors disabled:opacity-60",
                    dragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40",
                )}
            >
                <UploadCloudIcon className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Drop files, or click to add</span>
                <span className="text-xs text-gray-500">PDF, DOCX, TXT, CSV, XLSX. Pick as many as you like.</span>
            </button>
            <button
                type="button"
                disabled={disabled || writesPaused}
                onClick={onPaste}
                className="flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-300 bg-white px-4 py-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40 disabled:opacity-60"
            >
                <ClipboardIcon className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-900">Paste text</span>
                <span className="text-xs text-gray-500">An email, opening hours, a price list you copied. Ctrl V works anywhere in this step.</span>
            </button>
        </div>
    );
}

export interface KnowledgeNoteDraft {
    title: string;
    text: string;
    content_as_of: string;
}

/**
 * The note form, opened in place under the tiles.
 *
 * It STAYS OPEN after each add, because the thing people actually do is paste
 * four separate messages in a row. A form that closed on every save would make
 * the common case four round trips through the tile, and the hint says so
 * rather than leaving someone to discover it.
 */
export function KnowledgeNoteBox({
    seedText,
    nextIndex,
    onAdd,
    onDone,
}: {
    /** Clipboard text, when the box was opened by a paste rather than a click. */
    seedText?: string;
    /** Feeds the `Pasted note N` auto-title, so a blank title is never blank. */
    nextIndex: number;
    onAdd: (draft: KnowledgeNoteDraft) => Promise<boolean>;
    onDone: () => void;
}) {
    const today = new Date().toISOString().slice(0, 10);
    const [title, setTitle] = useState("");
    const [text, setText] = useState(seedText ?? "");
    const [asOf, setAsOf] = useState(today);
    const [saving, setSaving] = useState(false);
    const [added, setAdded] = useState(0);
    const textRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => { textRef.current?.focus(); }, []);

    const submit = async () => {
        if (!text.trim() || saving) return;
        setSaving(true);
        const ok = await onAdd({
            title: title.trim() || `Pasted note ${nextIndex + added}`,
            text: text.trim(),
            content_as_of: asOf,
        });
        setSaving(false);
        if (!ok) return;
        // Cleared and refocused, not closed: the next paste goes straight in.
        setAdded((n) => n + 1);
        setTitle("");
        setText("");
        textRef.current?.focus();
    };

    return (
        <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Title</span>
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`Pasted note ${nextIndex + added}`}
                        className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                    />
                </label>
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">As of</span>
                    <input
                        type="date"
                        value={asOf}
                        onChange={(e) => setAsOf(e.target.value)}
                        className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                    />
                </label>
            </div>
            <label className="mt-3 block">
                <span className="mb-1 block text-xs font-medium text-gray-700">Text</span>
                <textarea
                    ref={textRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={5}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500"
                    placeholder="Opening hours, a price list, an email the owner sent…"
                />
            </label>
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                    {added > 0 ? "Added. Paste another, or press Done." : " "}
                </span>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={onDone}>Done</Button>
                    <Button size="sm" writes disabled={!text.trim() || saving} onClick={() => void submit()}>
                        {saving ? <><Loader2Icon className="h-3.5 w-3.5 animate-spin" />Adding…</> : "Add"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/** `N sources · X files and Y notes`, the one count line both surfaces show. */
export function knowledgeCounts(sources: AeoKnowledgeSource[]): { total: number; files: number; notes: number; chars: number } {
    const files = sources.filter((s) => s.kind === "file").length;
    return {
        total: sources.length,
        files,
        notes: sources.length - files,
        chars: sources.reduce((n, s) => n + (s.charCount ?? 0), 0),
    };
}

export function KnowledgeCountLine({ sources, right }: { sources: AeoKnowledgeSource[]; right?: ReactNode }) {
    const c = knowledgeCounts(sources);
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span>
                {c.total} {c.total === 1 ? "source" : "sources"} · {c.files} {c.files === 1 ? "file" : "files"} and {c.notes} {c.notes === 1 ? "note" : "notes"}
            </span>
            {right}
        </div>
    );
}
